'use client';

/**
 * Cookie consent — GDPR/CCPA granular toggles with annual re-consent.
 *
 * Three categories:
 *  - necessary    : always on, required for the site to function (auth, CSRF)
 *  - analytics    : reserved for future analytics integration; off until opt-in
 *  - advertising  : gates the Google AdSense script — see AdSenseScript.tsx
 *
 * Persistence: a single JSON blob in localStorage. The storage key is
 * versioned (`cookie-consent-v2`) so a schema change re-prompts existing
 * users. v1 records (no `expires_at`) are not migrated — those users see
 * the modal again, which is the GDPR-safe behavior.
 *
 * Annual re-consent: every persisted choice carries an `expires_at`
 * (one year from acceptance, by default). On hydrate, if the timestamp
 * has passed we treat the record as undecided and re-open the modal.
 * This matches the practice most regulators expect: a user's consent
 * isn't valid forever.
 *
 * SSR: until the provider hydrates, `decided === false` and every
 * non-necessary category is treated as `false`. That keeps AdSense and any
 * future analytics script off during the brief pre-hydration window.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getApiBaseUrl } from '@/lib/api-url';

const STORAGE_KEY = 'cookie-consent-v2';

// One year (in ms) — picked because most jurisdictions treat consent as
// valid up to 12 months. If you want a different policy, change this and
// bump STORAGE_KEY to v3 so existing users get re-prompted under the new
// rule (otherwise their stale `expires_at` would still apply).
const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000;

export type ConsentCategories = {
  necessary: true;          // locked on
  analytics: boolean;
  advertising: boolean;
};

export type ConsentState = ConsentCategories & {
  decided: boolean;         // false until the user has saved a (non-expired) choice
  hydrated: boolean;        // false during SSR + first paint
  /** Unix ms when the current acceptance expires. 0 if never accepted. */
  expiresAt: number;
};

type Ctx = ConsentState & {
  /** Open the modal in "edit" mode — user already decided once. */
  openSettings: () => void;
  /** Modal-visible flag. True on first visit (auto-shown) or after openSettings(). */
  modalOpen: boolean;
  /** Close the modal without changing prefs (only allowed if already decided). */
  closeModal: () => void;
  /** Persist the prefs the user picked. Sets `decided=true` and closes modal. */
  save: (next: { analytics: boolean; advertising: boolean }) => void;
  /** Convenience: opt in to everything. */
  acceptAll: () => void;
  /** Convenience: keep only necessary. */
  rejectNonEssential: () => void;
};

const defaults: Omit<ConsentState, 'hydrated'> = {
  necessary: true,
  analytics: false,
  advertising: false,
  decided: false,
  expiresAt: 0,
};

const CookieConsentContext = createContext<Ctx | null>(null);

type Persisted = {
  analytics: boolean;
  advertising: boolean;
  decided: boolean;
  /** Unix ms timestamp when this consent record was created. */
  acceptedAt: number;
  /** Unix ms timestamp when this consent record stops being valid. */
  expiresAt: number;
  /**
   * Anonymous random identifier (UUID v4) generated on the first save.
   * Persisted so subsequent decisions from the same browser correlate in
   * the server-side audit log. Never sent to third parties; used only to
   * tie multiple decisions to one browser before the user logs in.
   */
  sessionId?: string;
};

const API_URL = getApiBaseUrl();

/**
 * Fire-and-forget POST to the server consent log. We do NOT await it from
 * the React `save()` flow — the user's own localStorage record is the
 * source of truth for what scripts to run. The server log is a regulator-
 * facing audit trail; if the network is down we don't want to block UX.
 */
function postConsentToServer(record: Persisted) {
  if (typeof window === 'undefined' || !record.sessionId) return;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const t = window.localStorage.getItem('access_token') || window.localStorage.getItem('ps_token');
    if (t) headers['Authorization'] = `Bearer ${t}`;
  } catch {
    /* ignore — guests don't have a token */
  }
  const body = {
    session_id: record.sessionId,
    categories: {
      necessary: true,
      analytics: record.analytics,
      advertising: record.advertising,
    },
    version: STORAGE_KEY,
  };
  // `keepalive` lets the browser finish the POST even if the user navigates
  // away immediately after clicking. Catch-and-ignore — log endpoint is
  // best-effort.
  try {
    fetch(`${API_URL}/consent`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => { /* swallow — best-effort log */ });
  } catch {
    /* swallow — best-effort log */
  }
}

function generateSessionId(): string {
  // Prefer the platform's CSPRNG — fall back to Math.random only if absent.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Best-effort RFC4122-ish; only used in environments without crypto.randomUUID.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function readPersisted(): Persisted | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    if (typeof parsed !== 'object' || parsed === null) return null;
    return {
      analytics: !!parsed.analytics,
      advertising: !!parsed.advertising,
      decided: !!parsed.decided,
      acceptedAt: typeof parsed.acceptedAt === 'number' ? parsed.acceptedAt : 0,
      expiresAt: typeof parsed.expiresAt === 'number' ? parsed.expiresAt : 0,
      sessionId: typeof parsed.sessionId === 'string' ? parsed.sessionId : undefined,
    };
  } catch {
    return null;
  }
}

function writePersisted(p: Persisted) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* ignore — private mode, quota, etc. */
  }
}

export function CookieConsentProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ConsentState>({
    ...defaults,
    hydrated: false,
  });
  const [modalOpen, setModalOpen] = useState(false);

  // Hydrate from localStorage once on mount. If no record, or the record
  // has expired (annual re-consent), auto-open the modal so the user is
  // forced to make a fresh choice before any non-necessary cookies fire.
  useEffect(() => {
    const persisted = readPersisted();
    const now = Date.now();
    const isExpired = !!persisted && persisted.expiresAt > 0 && persisted.expiresAt < now;
    const isFresh = !!persisted && persisted.decided && !isExpired;

    if (isFresh && persisted) {
      setState({
        necessary: true,
        analytics: persisted.analytics,
        advertising: persisted.advertising,
        decided: true,
        expiresAt: persisted.expiresAt,
        hydrated: true,
      });
    } else {
      // No record OR expired — treat as undecided. The previous record
      // (if any) stays in localStorage but is ignored; `save()` overwrites
      // it as soon as the user makes a new choice.
      setState((s) => ({ ...s, hydrated: true }));
      setModalOpen(true);
    }
  }, []);

  const save = useCallback((next: { analytics: boolean; advertising: boolean }) => {
    const now = Date.now();
    const expiresAt = now + CONSENT_TTL_MS;
    // Reuse the existing sessionId if we have one, so consecutive decisions
    // from the same browser correlate in the audit log. Generate a fresh
    // UUID on first save.
    const existing = readPersisted();
    const sessionId = existing?.sessionId || generateSessionId();
    const record: Persisted = {
      ...next,
      decided: true,
      acceptedAt: now,
      expiresAt,
      sessionId,
    };
    writePersisted(record);
    setState({
      necessary: true,
      analytics: next.analytics,
      advertising: next.advertising,
      decided: true,
      expiresAt,
      hydrated: true,
    });
    setModalOpen(false);
    // Best-effort regulator-facing log. Never blocks the UX.
    postConsentToServer(record);
  }, []);

  const acceptAll = useCallback(() => {
    save({ analytics: true, advertising: true });
  }, [save]);

  const rejectNonEssential = useCallback(() => {
    save({ analytics: false, advertising: false });
  }, [save]);

  const openSettings = useCallback(() => setModalOpen(true), []);

  const closeModal = useCallback(() => {
    // Only allow closing without saving if the user has already decided once.
    // First-time visitors must make a choice.
    setState((s) => {
      if (s.decided) setModalOpen(false);
      return s;
    });
  }, []);

  const value = useMemo<Ctx>(
    () => ({
      ...state,
      modalOpen,
      openSettings,
      closeModal,
      save,
      acceptAll,
      rejectNonEssential,
    }),
    [state, modalOpen, openSettings, closeModal, save, acceptAll, rejectNonEssential],
  );

  return (
    <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): Ctx {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error('useCookieConsent must be used inside <CookieConsentProvider>');
  return ctx;
}
